/**
 * City/country search for the timezone pickers. Our option list carries one
 * city per offset ("Kuala Lumpur", never "Manila"), so "manila" matched nothing
 * (ENG-3652). Indexes the full IANA place set instead and resolves a term to
 * the live GMT offsets those places sit on.
 */
import moment from 'moment-timezone';

/** Shorter terms ("in", "ph") substring-match half the index and flood the menu. */
export const MIN_PLACE_SEARCH_LENGTH = 3;

/** Live `GMT±HH:MM` for a zone — the same shape the option labels render. */
export const formatLiveGmtOffset = (
  timezone: string | undefined | null
): string => {
  if (!timezone || !moment.tz.zone(timezone)) return '';

  const offsetInMinutes = moment().tz(timezone).utcOffset();
  const sign = offsetInMinutes < 0 ? '-' : '+';
  const hours = Math.floor(Math.abs(offsetInMinutes) / 60);
  const minutes = Math.abs(offsetInMinutes) % 60;

  return `GMT${sign}${String(hours).padStart(2, '0')}:${String(
    minutes
  ).padStart(2, '0')}`;
};

/** Everything after the region prefix: "America/Argentina/Buenos_Aires" →
 * "Argentina Buenos Aires", so city and grouping both match. */
const cityTextForZone = (zone: string): string => {
  const segments = zone.split('/');
  const place = segments.length > 1 ? segments.slice(1).join(' ') : zone;
  return place.replace(/_/g, ' ');
};

const countryNamesByZone = (): Map<string, string[]> => {
  // Turns moment's ISO codes into the words people type ("PH" → "Philippines").
  // Guarded so a missing Intl degrades to code-only rather than breaking.
  let regionNames: Intl.DisplayNames | null = null;
  try {
    regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
  } catch {
    regionNames = null;
  }

  const byZone = new Map<string, string[]>();
  for (const countryCode of moment.tz.countries()) {
    let countryName = countryCode;
    try {
      countryName = regionNames?.of(countryCode) || countryCode;
    } catch {
      countryName = countryCode;
    }

    // Europe/Zurich covers CH, DE and LI — keep every name.
    for (const zone of moment.tz.zonesForCountry(countryCode)) {
      const names = byZone.get(zone);
      if (names) names.push(countryName);
      else byZone.set(zone, [countryName]);
    }
  }
  return byZone;
};

interface TimezonePlace {
  zone: string;
  /** Lowercased city + country text, pre-joined for substring matching. */
  searchText: string;
}

let placesCache: TimezonePlace[] | null = null;

/** Built once on first search — ~600 zones, so not worth paying at import. */
const getTimezonePlaces = (): TimezonePlace[] => {
  if (placesCache) return placesCache;

  const countriesByZone = countryNamesByZone();
  placesCache = moment.tz.names().map((zone) => ({
    zone,
    searchText: [cityTextForZone(zone), ...(countriesByZone.get(zone) ?? [])]
      .join(' ')
      .toLowerCase(),
  }));
  return placesCache;
};

const EMPTY_OFFSETS: ReadonlySet<string> = new Set();

/** Keystroke-level memo — `filterOption` runs once per option per keypress. */
const offsetsByTerm = new Map<string, ReadonlySet<string>>();
const MAX_MEMOISED_TERMS = 50;

/** Offsets of every place matching `term` — "manila" → `{ 'GMT+08:00' }`. */
export const findGmtOffsetsForPlaceSearch = (
  term: string
): ReadonlySet<string> => {
  const searchTerm = term?.trim().toLowerCase() ?? '';
  if (searchTerm.length < MIN_PLACE_SEARCH_LENGTH) return EMPTY_OFFSETS;

  const memoised = offsetsByTerm.get(searchTerm);
  if (memoised) return memoised;

  const offsets = new Set<string>();
  for (const place of getTimezonePlaces()) {
    if (!place.searchText.includes(searchTerm)) continue;
    const offset = formatLiveGmtOffset(place.zone);
    if (offset) offsets.add(offset);
  }

  if (offsetsByTerm.size >= MAX_MEMOISED_TERMS) offsetsByTerm.clear();
  offsetsByTerm.set(searchTerm, offsets);
  return offsets;
};

/** The react-select option payload every timezone picker builds. */
export interface TimezoneSearchOptionData {
  label?: string;
  value?: string;
  offset?: string;
  abbreviation?: string;
  fullName?: string;
  shortName?: string;
}

/** Does this option's own text (abbreviation / names / label) carry the term? */
const hasDirectHit = (
  data: TimezoneSearchOptionData,
  searchTerm: string
): boolean =>
  [data.abbreviation, data.fullName, data.shortName, data.label].some((field) =>
    field?.toLowerCase().includes(searchTerm)
  );

export type TimezoneSearchFilter = (
  _option: { data?: TimezoneSearchOptionData } | null | undefined,
  _inputValue: string
) => boolean;

/**
 * Builds a react-select `filterOption` over `options`. Direct text matches win
 * outright; place expansion is a FALLBACK, used only when the term matched no
 * option directly. Without that gate "ist" pulled in all 36 zones sharing an
 * offset with Istanbul/Pakistan/Kazakhstan instead of just Kolkata.
 *
 * Needs the whole list because react-select judges one option at a time and
 * "did anything else match?" is not answerable from a single row.
 */
export const createTimezoneSearchFilter = (
  options: readonly TimezoneSearchOptionData[]
): TimezoneSearchFilter => {
  // kula-ai's Select rebuilds each option as {value, label, description, …}
  // before react-select sees it, dropping abbreviation/fullName/shortName —
  // so "sgt" could never match there. Resolve the row back to our own record
  // through `value`, the one field every wrapper preserves.
  const byValue = new Map<string, TimezoneSearchOptionData>();
  for (const option of options) {
    if (option?.value) byValue.set(option.value, option);
  }

  // Per-term, per-list memo: `filterOption` runs once per option per keypress.
  const directMatchByTerm = new Map<string, boolean>();

  const listHasDirectMatch = (searchTerm: string): boolean => {
    const memoised = directMatchByTerm.get(searchTerm);
    if (memoised !== undefined) return memoised;

    const found = options.some((data) => hasDirectHit(data, searchTerm));
    if (directMatchByTerm.size >= MAX_MEMOISED_TERMS) directMatchByTerm.clear();
    directMatchByTerm.set(searchTerm, found);
    return found;
  };

  return (option, inputValue) => {
    const searchTerm = inputValue?.trim().toLowerCase() ?? '';
    if (!searchTerm) return true;

    const rendered = option?.data ?? {};
    const data =
      (rendered.value ? byValue.get(rendered.value) : undefined) ?? rendered;
    if (hasDirectHit(data, searchTerm)) return true;
    if (listHasDirectMatch(searchTerm)) return false;

    const offset = data.offset || formatLiveGmtOffset(data.value);
    return !!offset && findGmtOffsetsForPlaceSearch(searchTerm).has(offset);
  };
};

/** Test seam — the place index and memo are module-level caches. */
export const resetTimezonePlaceSearchCaches = (): void => {
  placesCache = null;
  offsetsByTerm.clear();
};
